import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useViewOffersQuery } from "../../API/Offers";
import { defaultParams } from "@utils/global.constants";
import { useUpdateApplicationStatusMutation } from "../../API/Offers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import groupedcircles from "../../Assets/Images/circle-grp.png";
import dropdown from "../../Assets/Images/dropdown-polygon.png";
import "./PendingOfferApplicants.css";
import useDebounce from "@hooks/useDebounce";
import { useSocket } from "@utils/socket/socketContext";
import axios from "axios";
import envConfig from "../../environment";
import storage from "@utils/storage";
import { changeStatus } from "../../API/Offers";
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { Modal } from 'bootstrap';
import { jsPDF } from 'jspdf';
import logo from "../../Assets/Images/Lendriva_logo.jpg";
import { emailSignedContractConfirmation } from "../../API/Contract";
import { downloadFromUrl } from "@utils/downloadFile";

type Contract = {
  uploadedAt: string | number | Date;
  stage: string;
};

type Application = {
  user: any;
  contracts: Contract[];
  uuid: string;
  lenderId: string;
  borrowerId: string;
  status: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  offer: {
    title: string
    loanAmount: string;
    totalReturnAmount: string
    interestRate: string;
    loanDuration: string;
    termsAndConditions: string;
    status: string;
    createdAt: string
  };
  borrower: {
    uuid: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    creditScore: string;
    address: string;
    pinCode: string;
    country: string;
    state: string;
    city: string;
    region: string;
    profileImage: string;
  }
};
function PendingOfferApplicants() {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocket();
  const offerId = location.state?.offerId;
  const [params, setParams] = useState(defaultParams);
  const debouncedSearch = useDebounce(params.search, 500);
  const [selectedSortLabel1, setSelectedSortLabel1] = useState("Newest First");
  const [selectedSortLabel2, setSelectedSortLabel2] = useState("High Credit");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [, setIsSigned] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const Id = storage.getUser();
  const userId = Id.user.userId;
  const user = Id.user;
  const [contractUrl, setContractUrl] = useState<{ [key: string]: string }>({});
  const [selectedContractUrl, setSelectedContractUrl] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [dropdownOpen1, setDropdownOpen1] = useState(false);
  const [dropdownOpen2, setDropdownOpen2] = useState(false);
  const { data, refetch } =
    useViewOffersQuery({
      offerId: offerId,
      params: { ...params, search: debouncedSearch },
    });


  const { mutate: updateStatus, isLoading: isUpdating } =
    useUpdateApplicationStatusMutation({
      onSuccess: async (data) => {
        toast.success("Application status updated!");

        const application = data?.data;
        const status = application?.status;
        const borrowerId = application?.borrowerId;
        const lenderId = application?.data?.lenderId;
        const lenderData = application?.lender;
        const offerTitle = application?.data?.title;
        const lenderName = lenderData?.firstName;

        const statusMessages: Record<string, string> = {
          approved: `Your loan application for Offer: ${offerTitle} has been Approved.`,
          rejected: `Your loan application for Offer: ${offerTitle} has been Rejected.`,
          underReview: `Your loan application for Offer: ${offerTitle} is Under Review.`,
          submitted: `Your loan application for Offer: ${offerTitle} has been Submitted.`,
          completed: `The offer "${offerTitle}" has been closed by the "${user.firstName}". The deal is now complete.`,

        };

        const notificationTitle = `Application Status Updated: ${status}`;
        const notificationMessage = statusMessages[status];

        try {
          // ✅ 1. Save notification to DB first
          await axios.post(
            `${envConfig.apiUrl}/notification/create-offer-notification`,
            {
              senderId: lenderId,
              senderType: "lender",
              receiverId: borrowerId,
              receiverType: "borrower",
              title: notificationTitle,
              notification: notificationMessage,
              isGlobal: false,
            },
            {
              headers: {
                Authorization: `Bearer ${storage.getToken()}`,
              },
            }
          );

          // ✅ 2. Emit socket event after notification is saved
          if (socket?.connected && borrowerId && lenderId && status) {
            socket.emit("application status", {
              borrowerId,
              lenderId,
              status,
              offerTitle,
              lenderName,
              notificationMessage,
            });
          }
        } catch (apiErr) {
          console.error("Failed to save application notification:", apiErr);
        }

        // ✅ 3. Navigate based on status
        switch (status) {
          case "approved":
            navigate("/app/approved-offers");
            break;
          case "underReview":
            navigate("/app/in-review-offers");
            break;
          case "completed":
            navigate("/app/completed-offers");
            break;
          default:
            break;
        }

        refetch();
      },

      onError: (error: any) => {
        // Try to extract server response
        const statusCode = error?.response?.data?.statusCode;
        const serverMessage = error?.response?.data?.message;
        const errorData = error?.response?.data?.data;

        // Handle insufficient wallet balance
        if (statusCode === 402 && errorData === "InsufficientWalletBalance") {
          toast.error("Insufficient wallet balance to approve this application, add funds to your wallet!", {
            className: "custom-toast-error",
            style: {
              fontWeight: "bold",
              padding: "1rem",
              borderRadius: "8px",
            },
          });
          return;
        }

        // Generic fallback error
        toast.error(`Error updating status: ${serverMessage || error.message}`);
      }
    });

  const handleStatusUpdate = (applicationId: string, borrowerId: string, newStatus: string) => {
    const validStatuses = ["submitted", "approved", "rejected", "underReview", "completed"];
    if (!validStatuses.includes(newStatus)) {
      toast.error("Invalid status selected");
      return;
    }

    const payload = {
      applicationId,
      borrowerId,
      offerId,
      status: newStatus,
    };
    updateStatus(payload);
  };

  const applications = data?.applications || [];
  const hasApproved = applications.some((app: Application) => app.status === "approved");
  const displayApplications = hasApproved
    ? applications.filter((app: Application) => app.status === "approved")
    : applications;

  useEffect(() => {
    refetch();
  }, [debouncedSearch, refetch]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setIsSigned(false);
      setSignatureDataUrl(null);
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let drawing = true;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);

    const draw = (moveEvent: MouseEvent) => {
      if (!drawing) return;
      ctx.lineTo(moveEvent.clientX - rect.left, moveEvent.clientY - rect.top);
      ctx.stroke();
    };

    const stopDrawing = () => {
      drawing = false;
      setIsSigned(true);
      setSignatureDataUrl(canvas.toDataURL());
      canvas.removeEventListener('mousemove', draw);
      canvas.removeEventListener('mouseup', stopDrawing);
    };

    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
  };

  const handleDownloadPDF = (application: Application): Blob => {
    if (!data || !signatureDataUrl || !application) return;

    const doc = new jsPDF();
    const x = 10;
    const fullWidth = 205;

    const {
      loanAmount, interestRate, loanDuration,
      totalReturnAmount, lender,
    } = data;

    const borrower = application.borrower;

    // Logo
    const logoMaxWidth = 40;
    const imgProps = doc.getImageProperties(logo);
    const aspectRatio = imgProps.height / imgProps.width;
    const logoHeight = logoMaxWidth * aspectRatio;
    const logoX = fullWidth - logoMaxWidth - 10;
    const logoY = 10;
    doc.addImage(logo, 'PNG', logoX, logoY, logoMaxWidth, logoHeight);

    // Title
    let y = logoY + logoHeight + 10;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Loan Agreement', x + 70, y);
    y += 15;

    // Contract Intro
    doc.setFontSize(13);
    doc.setFont('times', 'normal');
    const contractText = `This loan Agreement ("Agreement") is made and entered into on this ${new Date().toLocaleDateString()} by and between:`;
    const splitText = doc.splitTextToSize(contractText, 190);
    doc.text(splitText, x, y);
    y += splitText.length * 6 + 10;

    // Lender Details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Lender:', x + 2, y + 7);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    y += 15;
    doc.text(`Name: ${lender.fullName}`, x + 2, y);
    y += 7;
    doc.text(`Email: ${lender.email}`, x + 2, y);
    y += 7;
    doc.text(`Phone: ${lender.phoneNumber}`, x + 2, y);
    y += 7;
    doc.text(`Address: ${lender.address}, ${lender.city}, ${lender.state}`, x + 2, y);
    y += 14;

    // Borrower
    doc.setFont('helvetica', 'bold');
    doc.text('Borrower:', x + 2, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${borrower.fullName}`, x + 2, y);
    y += 7;
    doc.text(`Email: ${borrower.email}`, x + 2, y);
    y += 7;
    doc.text(`Phone: ${borrower.phoneNumber}`, x + 2, y);
    y += 7;
    doc.text(`Address: ${borrower.address}, ${borrower.city}, ${borrower.state}`, x + 2, y);
    y += 14;

    // Loan Details
    doc.setFont('times', 'normal');
    doc.text(`Loan Amount: £${loanAmount}`, x, y);
    y += 7;
    doc.text(`Interest Rate: ${interestRate}%`, x, y);
    y += 7;
    doc.text(`Loan Duration: ${loanDuration} months`, x, y);
    y += 7;
    doc.text(`Total Repayment: £${totalReturnAmount}`, x, y);
    y += 14;

    // Signature Section
    doc.setFont('helvetica', 'bold');
    doc.text('Signatures', x, y);
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.line(x, y + 5, x + 60, y + 5);
    doc.text('Lender Signature', x, y + 10);

    doc.line(x + 110, y + 5, x + 170, y + 5);
    doc.text('Borrower Signature', x + 110, y + 10);

    // Insert Signature Image
    doc.addImage(signatureDataUrl, 'PNG', x + 6, y - 10, 50, 20);

    // Date
    doc.text(`Agreement Date: ${new Date().toLocaleDateString()}`, x, y + 25);

    return doc.output('blob');
  };

  const generateContractPDF = (application: Application): jsPDF | null => {
    if (!data || !application) return null;

    const doc = new jsPDF();
    const x = 10;
    const fullWidth = 205;

    const {
      loanAmount, interestRate, loanDuration,
      totalReturnAmount, lender,
    } = data;
    const borrower = application.borrower;

    // Logo
    const logoMaxWidth = 40;
    const imgProps = doc.getImageProperties(logo);
    const aspectRatio = imgProps.height / imgProps.width;
    const logoHeight = logoMaxWidth * aspectRatio;
    const logoX = fullWidth - logoMaxWidth - 10;
    const logoY = 10;
    doc.addImage(logo, 'PNG', logoX, logoY, logoMaxWidth, logoHeight);

    let y = logoY + logoHeight + 10;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Loan Agreement', x + 70, y);
    y += 15;

    doc.setFontSize(13);
    doc.setFont('times', 'normal');
    const contractText = `This loan Agreement ("Agreement") is made and entered into on this ${new Date().toLocaleDateString()} by and between:`;
    const splitText = doc.splitTextToSize(contractText, 190);
    doc.text(splitText, x, y);
    y += splitText.length * 6 + 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Lender:', x + 2, y + 7);
    y += 15;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${lender.fullName}`, x + 2, y);
    y += 7;
    doc.text(`Email: ${lender.email}`, x + 2, y);
    y += 7;
    doc.text(`Phone: ${lender.phoneNumber}`, x + 2, y);
    y += 7;
    doc.text(`Address: ${lender.address}, ${lender.city}, ${lender.state}`, x + 2, y);
    y += 14;

    doc.setFont('helvetica', 'bold');
    doc.text('Borrower:', x + 2, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${borrower.fullName}`, x + 2, y);
    y += 7;
    doc.text(`Email: ${borrower.email}`, x + 2, y);
    y += 7;
    doc.text(`Phone: ${borrower.phoneNumber}`, x + 2, y);
    y += 7;
    doc.text(`Address: ${borrower.address}, ${borrower.city}, ${borrower.state}`, x + 2, y);
    y += 14;

    doc.setFont('times', 'normal');
    doc.text(`Loan Amount: £${loanAmount}`, x, y);
    y += 7;
    doc.text(`Interest Rate: ${interestRate}%`, x, y);
    y += 7;
    doc.text(`Loan Duration: ${loanDuration} months`, x, y);
    y += 7;
    doc.text(`Total Repayment: £${totalReturnAmount}`, x, y);
    y += 14;

    doc.setFont('helvetica', 'bold');
    doc.text('Signatures', x, y);
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.line(x, y + 5, x + 60, y + 5);
    doc.text('Lender Signature', x, y + 10);

    doc.line(x + 110, y + 5, x + 170, y + 5);
    doc.text('Borrower Signature', x + 110, y + 10);

    doc.text(`Agreement Date: ${new Date().toLocaleDateString()}`, x, y + 25);

    return doc;
  };

  const handleSendContractClick = (app: Application) => {
    setSelectedApplication(app);

    const modalElement = document.getElementById('signModal');
    if (modalElement) {
      const modal = new Modal(modalElement);
      const isCompleted = data.lender?.isProfileCompleted ?? false;

      setIsProfileComplete(isCompleted); // properly reflect actual state
      modal.show();
    }
  };

  const uploadSignedPDF = async () => {
    if (!selectedApplication) {
      toast.error("No application selected for contract upload.");
      return;
    }

    setBtnLoading(true); // ⬅️ Start loading

    try {
      const pdfBlob = await handleDownloadPDF(selectedApplication);
      if (!pdfBlob) {
        toast.error("Failed to generate PDF.");
        return;
      }

      const formData = new FormData();
      formData.append('file', pdfBlob, `Loan_Contract_${data.title}.pdf`);
      formData.append('offerId', data.uuid);
      formData.append('stage', 'signed_by_lender');
      formData.append('lenderUuid', data.lender.uuid);
      formData.append('borrowerUuid', selectedApplication.borrower.uuid);
      formData.append('applicationId', selectedApplication.uuid);

      const response = await axios.post(`${envConfig.apiUrl}/user/upload-contract`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        toast.success('Contract sent to borrower!');

        try {
          await emailSignedContractConfirmation({
            offerId: data.uuid,
            applicationId: selectedApplication.uuid,
            borrowerUuid: selectedApplication.borrower.uuid,
            lenderUuid: data.lender.uuid,
            stage: "signed_by_lender",
          });
        } catch {
          // Backend email endpoint may not be available yet
        }

        setSelectedApplication(null);

        const modalElement = document.getElementById('signModal');
        if (modalElement) {
          const modal = Modal.getInstance(modalElement);
          modal?.hide();
          refetch();
        }

        // 🔔 Notification logic
        const borrowerId = selectedApplication?.borrowerId;
        const lenderId = userId;
        const offerTitle = data?.title;
        const notificationTitle = "Contract Generated";
        const notificationMessage = `A contract has been sent by "${user.firstName}" regarding the loan offer "${offerTitle}", based on your application.`;

        await axios.post(
          `${envConfig.apiUrl}/notification/create-offer-notification`,
          {
            senderId: lenderId,
            senderType: "lender",
            receiverId: borrowerId,
            receiverType: "borrower",
            title: notificationTitle,
            notification: notificationMessage,
            isGlobal: false,
          },
          {
            headers: {
              Authorization: `Bearer ${storage.getToken()}`,
            },
          }
        );

        if (socket?.connected) {
          socket.emit("contract generated", {
            borrowerId,
            lenderId,
            offerTitle,
            notificationMessage,
          });
        }
      } else {
        toast.error(`Upload failed: ${response.data?.message || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Error uploading file.');
    } finally {
      setBtnLoading(false);
    }
  };

  const fetchContractForApp = async (app: Application) => {
    try {
      const res = await axios.get(`${envConfig.apiUrl}/user/borrower-contract`, {
        params: {
          borrowerUuid: app.borrower.uuid,
          offerId: offerId,
        },
      });
      if (res?.data?.fileUrl) {
        const url = res.data.fileUrl;
        setContractUrl((prev) => ({ ...prev, [app.uuid]: url }));
        return url; // return the fetched URL
      } else {
        toast("No contract found for this application.");
        return null;
      }
    } catch (error) {
      toast("Failed to load contract.");
      return null;
    }
  };




  useEffect(() => {
    const modalElement = document.getElementById('pdfModal');
    if (!modalElement) return;

    modalElement.addEventListener('hidden.bs.modal', () => {
      setSelectedContractUrl(null);
    });

    return () => {
      modalElement.removeEventListener('hidden.bs.modal', () => {
        setSelectedContractUrl(null);
      });
    };
  }, []);

  const handleViewContract = (app: Application) => {
    const doc = generateContractPDF(app);
    if (!doc) {
      toast.error("Could not generate contract preview.");
      return;
    }

    const blob = doc.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    // Option 1: open in a new tab
    window.open(blobUrl, '_blank');

    // Option 2: show in modal via iframe
    // setSelectedContractUrl(blobUrl);
    // const modalElement = document.getElementById('pdfModal');
    // if (modalElement) {
    //   const modal = new Modal(modalElement);
    //   modal.show();
    // }
  };



  return (
    <div className="content pending-applicant-section">
      <div className="outer-box-wrapper mt-0">
        <div className="title-search-block d-flex align-items-center justify-content-between">
          <div className="input-btn-fields d-flex align-items-center">
            <button className="back-btn mb-4" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
        <div className="title-search-block d-flex align-items-center justify-content-between">
        </div>
        <div className="completed-box pending-offers position-relative overflow-hidden">
          <div className="deactive-btn-blk d-flex align-items-center justify-content-between">
            <h4 className="m-0">{data?.title || "N/A"}</h4>
            <div>
              {data?.status !== "inActive" && !applications.some((app: Application) => app.status === "approved") && (
                <button
                  className="deactivate"
                  type="button"
                  onClick={() => {
                    const modal = new Modal(document.getElementById('deactivateModal'));
                    modal.show();
                  }}
                >
                  Deactivate Offer
                </button>
              )}
              <div className="modal fade" id="deactivateModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered sidebar-modal">
                  <div className="modal-content">
                    <div className="delete-popup p-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#f8d7da" stroke="#842029" strokeWidth="2" />
                        <line x1="12" y1="7" x2="12" y2="12" stroke="#842029" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1.2" fill="#842029" />
                      </svg>
                      <h6>Are you sure you want to deactivate this offer?</h6>
                      <div className="confirmation-btn d-flex align-items-center justify-content-center mb-4">
                        <div className="btn-confirm">
                          <button
                            type="button"
                            className="btn-outline"
                            onClick={() => {
                              changeStatus({ offerId, status: "inactive" })
                                .then(() => {
                                  toast.success("Offer deactivated successfully");
                                  navigate("/app/canceled-offers");
                                })
                                .catch((error) => {
                                  toast.error(`Error deactivating offer: ${error.message}`);
                                });
                            }}
                            data-bs-dismiss="modal"
                          >
                            Yes
                          </button>
                        </div>
                        <div className="btn-confirm">
                          <button type="button" className="button-lender no-btn" data-bs-dismiss="modal">
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cannot deactivate modal */}
          <div className="modal fade" id="cannotDeactivateModal" tabIndex={-1} aria-labelledby="cannotDeactivateModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered sidebar-modal">
              <div className="modal-content">
                <div className="delete-popup">
                  <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" fill="#f8d7da" stroke="#842029" strokeWidth="2" />
                    <line x1="12" y1="7" x2="12" y2="12" stroke="#842029" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="16" r="1.2" fill="#842029" />
                  </svg>
                  <h6>You cannot deactivate this offer as it currently has approved applications.</h6>
                  <div className="confirmation-btn d-flex align-items-center justify-content-center justify-content-center mb-4">
                    <div className="btn-confirm">
                      <button type="button" className="button-lender no-btn" data-bs-dismiss="modal">
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="loan-info d-flex align-items-center gap-3 flex-wrap">
            <div className="loan-content">
              Loan Amount: <span>£{data?.loanAmount || "0.00"}</span>
            </div>
            <div className="vr-line"></div>
            <div className="loan-content">
              Interest: <span>{data?.interestRate || "0"}%</span>
            </div>
            <div className="vr-line"></div>
            <div className="loan-content">
              Date:{" "}
              <span>
                {new Date(data?.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
            <div className="vr-line"></div>
            <div className="loan-content">
              Loan Duration: <span>{data?.loanDuration || "N/A"} months</span>
            </div>
          </div>
          <div className="description">
            <div className="loan-content">
              Description:{" "}
              <span>
                {data?.termsAndConditions || "N/A"}
              </span>
            </div>
          </div>
          {/* <div className="grouped-circle position-absolute">
            <img src={groupedcircles} alt="" className="img-fluid" />
          </div> */}
        </div>

        {applications.length > 0 ? (
          <div className="alert">
            <div className="input-btn-fields pending-offer-input d-flex align-items-center justify-content-between">
              <div className="search-box">
              </div>


              <div className="dropdown-block gap-3 d-flex align-items-center position-relative">
                {/* First Dropdown: Sort by Date / Loan Amount */}
                <div className="position-relative me-3">
                  <button
                    className="dropdown-toggle offer-dropdown d-flex justify-content-between align-items-center"
                    onClick={() => setDropdownOpen1(prev => !prev)}
                  >
                    <div className="ofr-date text-truncate">{selectedSortLabel1}</div>
                    <div className="dropdown-polygon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="16" height="16" fill="#FBFCFF" />
                        <path d="M7.99996 12.5602C7.7132 12.5602 7.42649 12.4507 7.20787 12.2322L0.328226 5.35249C-0.109409 4.91486 -0.109409 4.20531 0.328226 3.76785C0.765684 3.33039 1.47509 3.33039 1.91276 3.76785L7.99996 9.8554L14.0872 3.76806C14.5248 3.33061 15.2342 3.33061 15.6716 3.76806C16.1094 4.20552 16.1094 4.91507 15.6716 5.35271L8.79205 12.2324C8.57332 12.451 8.2866 12.5602 7.99996 12.5602Z" fill="#202224" />
                      </svg>

                    </div>
                  </button>

                  {dropdownOpen1 && (
                    <ul className="dropdown-menu offer-dropdown-list show" style={{ display: 'block', position: 'absolute' }}>
                      {[
                        { label: "Newest First", sortBy: "createdAt", sortOrder: "DESC" },
                        { label: "Oldest First", sortBy: "createdAt", sortOrder: "ASC" },
                        { label: "Loan Amount High to Low", sortBy: "loanAmount", sortOrder: "DESC" },
                        { label: "Loan Amount Low to High", sortBy: "loanAmount", sortOrder: "ASC" },
                      ].map((item) => (
                        <li key={item.label}>
                          <button
                            className={`dropdown-item ${selectedSortLabel1 === item.label ? "active" : ""}`}
                            onClick={() => {
                              setParams(prev => ({
                                ...prev,
                                sortBy: item.sortBy,
                                sortOrder: item.sortOrder,
                              }));
                              setSelectedSortLabel1(item.label);
                              setDropdownOpen1(false);
                            }}
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Second Dropdown: Sort by Credit Score */}
                <div className="position-relative">
                  <button
                    className="dropdown-toggle offer-dropdown d-flex justify-content-between align-items-center"
                    onClick={() => setDropdownOpen2(prev => !prev)}
                  >
                    <div className="ofr-date text-truncate">{selectedSortLabel2}</div>
                    <div className="dropdown-polygon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="16" height="16" fill="#FBFCFF" />
                        <path d="M7.99996 12.5602C7.7132 12.5602 7.42649 12.4507 7.20787 12.2322L0.328226 5.35249C-0.109409 4.91486 -0.109409 4.20531 0.328226 3.76785C0.765684 3.33039 1.47509 3.33039 1.91276 3.76785L7.99996 9.8554L14.0872 3.76806C14.5248 3.33061 15.2342 3.33061 15.6716 3.76806C16.1094 4.20552 16.1094 4.91507 15.6716 5.35271L8.79205 12.2324C8.57332 12.451 8.2866 12.5602 7.99996 12.5602Z" fill="#202224" />
                      </svg>

                    </div>
                  </button>

                  {dropdownOpen2 && (
                    <ul className="dropdown-menu offer-dropdown-list show" style={{ display: 'block', position: 'absolute' }}>
                      {[
                        { label: "High Credit", sortBy: "borrower.creditScore", sortOrder: "DESC" },
                        { label: "Low Credit", sortBy: "borrower.creditScore", sortOrder: "ASC" },
                      ].map((item) => (
                        <li key={item.label}>
                          <button
                            className={`dropdown-item ${selectedSortLabel2 === item.label ? "active" : ""}`}
                            onClick={() => {
                              setParams(prev => ({
                                ...prev,
                                sortBy: item.sortBy,
                                sortOrder: item.sortOrder,
                              }));
                              setSelectedSortLabel2(item.label);
                              setDropdownOpen2(false);
                            }}
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>


            </div>
            <div className="accordion-box d-flex flex-column">
              {displayApplications?.map((app: Application, index: number) => (
                <div className="accordion" key={app.uuid} id={`accordion-${index}`}>
                  <div className="accordion-item overflow-hidden">
                    <h2 className="accordion-header">
                      <button
                        className="accordion-button d-flex align-items-center gap-3 justify-content-between"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#collapse-${index}`}
                        aria-expanded={index === 0 ? "true" : "false"}
                        aria-controls={`collapse-${index}`}
                      >
                        <div className="button-inner d-flex align-items-center gap-2">
                          <div className="acc-applicant-left-block">
                            <span>{app.borrower?.firstName ? app.borrower.firstName.charAt(0).toUpperCase() + app.borrower.firstName.slice(1) : "Andrew"}</span>
                            <span> {app.borrower?.lastName ? app.borrower.lastName.charAt(0).toUpperCase() + app.borrower.lastName.slice(1) : "Lender"}</span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="acc-applicant-left-block credit-score">
                            Credit Score: <span><span className="score">{app.borrower.creditScore || "N/A"}</span></span>
                          </div>
                          <div className="vr-line"></div>
                          <div className="acc-applicant-left-block credit-score">
                            Date:{" "}
                            <span>
                              {new Date(app?.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </span>
                          </div>

                        </div>
                        <div className="svg-dd-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 100 100">
                            <path d="M67.41 45.41l-2.82-2.82L50 57.17 35.41 42.59l-2.82 2.82L50 62.83z" fill="#2c3e50" />
                          </svg>
                        </div>
                      </button>
                    </h2>
                    <div
                      id={`collapse-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`}
                      data-bs-parent=".accordion-box"
                    >
                      <div className="accordion-body p-0">
                        <div className="applicant-info">
                          <div className="d-flex align-items-center justify-content-between settlement-block">
                            <div className="applicant-name-bloc d-flex align-items-center gap-3">
                              {/* {latestUserProfileImage ? (
                                <img
                                  src={`${envConfig.photoUrl}/public/profileImage/${latestUserProfileImage}`}
                                  alt="Profile"
                                  className="name-initials-box rounded-full object-cover w-full h-full"
                                />
                              ) : ( */}
                              {app.borrower.profileImage ? (
                                <img
                                  src={`${envConfig.photoUrl}/public/profileImage/${app.borrower.profileImage}`}
                                  alt="Profile"
                                  className="name-initials-box rounded-full object-cover w-full h-full"
                                />
                              ) : (
                                <div className="applicant-name-icon d-flex align-items-center justify-content-center">
                                  <span className="text-capitalize">{app.borrower?.firstName?.[0] || "A"}</span>
                                  <span className="text-capitalize">{app.borrower?.lastName?.[0] || "B"}</span>
                                </div>
                              )}

                              {/* )} */}
                              <div className="applicant-name"><span>{app.borrower?.firstName ? app.borrower.firstName.charAt(0).toUpperCase() + app.borrower.firstName.slice(1) : "Andrew"}</span> <span> {app.borrower?.lastName ? app.borrower.lastName.charAt(0).toUpperCase() + app.borrower.lastName.slice(1) : "Lender"}</span></div>
                            </div>
                            <div className="applicant-namests-blk d-flex justify-content-between align-items-center">
                              <div className="applicant-sts d-flex align-items-center gap-2 flex-wrap">

                                {/* Contract Button - only show if app.status is not rejected */}
                                {app.status !== "rejected" &&
                                  (Array.isArray(app.contracts) && app.contracts.length > 0 ? (() => {
                                    const latestContract = [...app.contracts].sort(
                                      (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
                                    )[0];

                                    if (latestContract.stage === 'signed_by_lender' || latestContract.stage === 'signed_by_borrower') {
                                      return (
                                        <button
                                          type="button"
                                          className="send-contract-btn"
                                          onClick={async () => {
                                            let url = contractUrl[app.uuid];

                                            if (!url) {
                                              url = await fetchContractForApp(app);
                                            }

                                            if (url) {
                                              try {
                                                await downloadFromUrl(
                                                  url,
                                                  `Loan_Contract_${data?.title || "agreement"}.pdf`
                                                );
                                              } catch {
                                                toast.error("Failed to download contract.");
                                              }
                                            } else {
                                              toast.error("Contract URL not available.");
                                            }
                                          }}
                                        >
                                          Download Contract
                                        </button>
                                      );
                                    } else {
                                      return (
                                        <button
                                          className="send-contract-btn"
                                          type="button"
                                          onClick={() => handleSendContractClick(app)}
                                        >
                                          Send Contract
                                        </button>
                                      );
                                    }
                                  })() : (
                                    <button
                                      className="send-contract-btn"
                                      type="button"
                                      onClick={() => handleSendContractClick(app)}
                                    >
                                      Send Contract
                                    </button>
                                  ))
                                }

                                {/* Status Buttons */}
                                {data?.status === "completed" ? (
                                  <p className="completed-status">Offer Completed</p>
                                ) : data?.status !== "inActive" && (
                                  <>
                                    {app.status === "rejected" ? (
                                      <p className="rejected-status">Rejected</p>
                                    ) : app.status === "approved" ? (
                                      <button
                                        className="accept-btn"
                                        disabled={isUpdating}
                                        onClick={() => handleStatusUpdate(app.uuid, app.borrowerId, "completed")}

                                      >
                                        Complete
                                      </button>
                                    ) : app.status === "underReview" ? (
                                      <>
                                        <button
                                          className={
                                            Array.isArray(app.contracts) &&
                                              app.contracts.length > 0 &&
                                              [...app.contracts].sort(
                                                (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
                                              )[0].stage === "signed_by_borrower"
                                              ? "accept-btn"
                                              : "d-none"
                                          }
                                          disabled={isUpdating}
                                          onClick={() => handleStatusUpdate(app.uuid, app.borrowerId, "approved")}
                                        >
                                          Approve
                                        </button>
                                        <button
                                          className="decline-btn"
                                          disabled={isUpdating}
                                          onClick={() => handleStatusUpdate(app.uuid, app.borrowerId, "rejected")}
                                        >
                                          Reject
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          className={
                                            Array.isArray(app.contracts) &&
                                              app.contracts.length > 0 &&
                                              [...app.contracts].sort(
                                                (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
                                              )[0].stage === "signed_by_borrower"
                                              ? "accept-btn"
                                              : "d-none"
                                          }
                                          disabled={isUpdating}
                                          onClick={() => handleStatusUpdate(app.uuid, app.borrowerId, "approved")}
                                        >
                                          Approve
                                        </button>
                                        <button
                                          className="pending-btn"
                                          disabled={isUpdating}
                                          onClick={() => handleStatusUpdate(app.uuid, app.borrowerId, "underReview")}
                                        >
                                          Under Review
                                        </button>
                                        <button
                                          className="decline-btn"
                                          disabled={isUpdating}
                                          onClick={() => handleStatusUpdate(app.uuid, app.borrowerId, "rejected")}
                                        >
                                          Reject
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="applicant-details">
                              <div className="location d-flex align-items-center gap-3 mb-3">
                                <div className="applicant-infofields">
                                  Email Address: <span>{app.borrower?.email || "N/A"}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="applicant-infofields">
                                  Contact number: <span>{app.borrower?.phoneNumber || "N/A"}</span>
                                </div>
                              </div>
                              <div className="location d-flex align-items-center gap-3 mb-3">
                                <div className="applicant-infofields ">
                                  Country: <span>{app.borrower?.country || "N/A"}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="applicant-infofields">
                                  State: <span>{app.borrower?.state || "N/A"}</span>
                                </div>
                                <div className="vr-line"></div>
                                <div className="applicant-infofields ">
                                  City: <span>{app.borrower?.city || "N/A"}</span>
                                </div>
                              </div>
                              <div>
                                <div className="applicant-infofields mb-3">
                                  Region: <span>{app.borrower?.region || "N/A"}</span>
                                </div>
                                <div className="applicant-infofields mb-3">
                                  PinCode: <span>{app.borrower?.pinCode || "N/A"}</span>
                                </div>
                                <div className="applicant-infofields mb-3">
                                  Address: <span>{app.borrower?.address || "N/A"}</span>
                                </div>

                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-5 mt-5 fw-semibold">
            You have no Application.
          </div>
        )}
        <div className="modal fade" id="signModal" tabIndex={-1} aria-labelledby="deactivateModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered sidebar-modal-box">
            <div className="modal-content">
              <div className="delete-popup">
                <div className="signature-section">
                  {!isProfileComplete ? (
                    <div className="p-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="none" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#f8d7da" stroke="#842029" strokeWidth="2" />
                        <line x1="12" y1="7" x2="12" y2="12" stroke="#842029" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1.2" fill="#842029" />
                      </svg>
                      <p className="applicant-infofields mb-10">
                        Please complete your profile before sending the contract.
                      </p>
                      <button
                        type="button"
                        className="send-contract-button"
                        onClick={() => {
                          const modalElement = document.getElementById('signModal');
                          navigate('/app/profile')
                          if (modalElement) {
                            const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
                            modalInstance.hide();
                          }
                        }}
                      >
                        Go to Profile Page
                      </button>
                    </div>

                  ) : (
                    <>
                      <p className="applicant-infofields mb-10">Please sign below before creating Contract:</p>
                      <canvas
                        ref={canvasRef}
                        width={300}
                        height={100}
                        style={{ border: '1px solid #ccc', borderRadius: '10px', marginBottom: '20px' }}
                        onMouseDown={startDrawing}
                      />
                      <div className="confirmation-btn d-flex align-items-center justify-content-center mb-4">
                        <div>
                          <div className="view-contract-button">
                            {(() => {
                              const contracts = selectedApplication?.contracts || [];
                              const latestContract = [...contracts].sort(
                                (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
                              )[0];

                              if (!latestContract || (latestContract.stage !== "signed_by_lender" && latestContract.stage !== "signed_by_borrower")) {
                                return (
                                  <button
                                    type="button"
                                    className="view-button-contract"
                                    onClick={() => handleViewContract(selectedApplication)}
                                  >
                                    View Contract
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              type="button"
                              className="send-contract-button"
                              onClick={uploadSignedPDF}
                              disabled={!signatureDataUrl || btnLoading}
                            >
                              {btnLoading ? 'Signing...' : 'Sign Contract and Send'}
                            </button>
                            <button type="button" className="download-pdf" onClick={clearSignature}>Clear Signature</button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>


          </div>
        </div>
        <div className="modal fade" id="pdfModal" tabIndex={-1} aria-labelledby="pdfModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered sidebar-modal">
            <div className="modal-content-pdf">
              <div className="delete-popup">
                <div className="signature-section">
                  <h5 className="view-contact mt-3">View Contract</h5>
                  {selectedContractUrl && (
                    <div className="pdf-viewer">
                      <iframe
                        key={selectedContractUrl}
                        src={selectedContractUrl}
                        width="100%"
                        height="600px"
                        title="Loan Contract"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
}

export default PendingOfferApplicants;
